<template>
	<el-input
		v-model="inputValue"
		class="batch-query-input"
		:size="size"
		:placeholder="placeholder || $t('inputKeywordsSearch')"
		@input="handleInput"
		@clear="handleClear"
		@keyup.enter.native="handleSearch"
		:style="{ width }"
		clearable
	>
		<div class="batch-query-input-suffix" slot="suffix">
			<i class="el-input__icon el-icon-search" @click="handleSearch"></i>
			<span class="batch-query-input-suffix-line"></span>
			<el-popover placement="bottom" trigger="click" v-model="visible" @after-leave="handleHide" @show="handleShow">
				<div class="batch-query-input-suffix-title" slot="reference">
					<span>
						{{ batchQueryValue.length > 0 ? batchQueryValue.length : $t("batchQuery") }}
					</span>
					<i v-show="batchQueryValue.length > 0" class="el-icon-close" @click.stop="handleClear"></i>
				</div>

				<div class="batch-query-input-suffix-content">
					<div class="batch-query-input-suffix-content-value">
						<div
							v-for="(item, index) in batchQueryValue"
							:key="index"
							class="batch-query-input-suffix-content-value-item"
						>
							<fixed-text :text="item" />
							<i class="el-icon-close" @click.stop="handleDeleteBatch(index)"></i>
						</div>
					</div>
					<el-input
						type="textarea"
						:autosize="{ minRows: 2 }"
						:placeholder="textAreaPlaceholder"
						v-model="batchInputValue"
						@input="handleBatchInput"
						@keyup.enter.native="handleBatchInput"
					/>
					<div class="batch-query-input-suffix-content-footer">
						<el-button @click="handleHide" size="mini">{{ $t("cancel") }}</el-button>
						<el-button
							size="mini"
							type="primary"
							plain
							@click="handleClearBatch"
							:disabled="batchQueryValue.length === 0 && !batchInputValue"
						>
							{{ $t("clear") }}
						</el-button>
						<el-button
							type="primary"
							size="mini"
							@click="handleConfirm"
							:disabled="batchQueryValue.length === 0 && !batchInputValue"
						>
							{{ $t("confirm") }}
						</el-button>
					</div>
				</div>
			</el-popover>
		</div>
	</el-input>
</template>

<script>
/**
 * 批量查询输入框组件
 * @param {String} size - 输入框尺寸
 * @param {String} placeholder - 输入框占位符
 * @param {String} width - 输入框宽度
 * @param {String | Array} value - 输入框值 string时为默认输入框的值，Array时为批量查询的值
 * @param {Number} maxLength - 最大多选数量
 * @param {String} textAreaPlaceholder - 文本域占位符
 * @event search - 搜索事件
 */

export default {
	name: "BatchQueryInput",
	props: {
		size: {
			type: String,
			default: "small"
		},
		placeholder: {
			type: String,
			default: ""
		},
		width: {
			type: String,
			default: "268px"
		},
		value: {
			type: [String, Array],
			default: ""
		},
		maxLength: {
			type: Number
		},
		textAreaPlaceholder: {
			type: String,
			default() {
				return this.$t("batchQueryTips");
			}
		}
	},
	data() {
		return {
			inputValue: "",
			visible: false,
			batchQueryValue: [],
			batchInputValue: ""
		};
	},
	methods: {
		handleInput(value) {
			if (this.value === undefined) {
				this.inputValue = value;
				this.$emit("input", this.inputValue);
			} else {
				this.$emit("input", value);
			}

			this.batchQueryValue = [];
		},
		handleClear() {
			this.inputValue = "";
			this.batchInputValue = "";
			this.batchQueryValue = [];
			this.$emit("input", this.inputValue);
			this.visible = false;
			this.handleSearch();
		},
		handleSearch() {
			this.$emit("search", this.inputValue);
		},
		handleShow() {
			this.visible = true;
		},
		handleHide() {
			this.visible = false;
		},
		handleBatchInput() {
			this.inputValue = "";
			const reg = /\s+/g;

			if (reg.test(this.batchInputValue)) {
				if (this.maxLength && this.batchQueryValue.length >= this.maxLength) {
					this.$message.error(this.$t("maxInputOrderNumber", { maxLength: this.maxLength }));
					this.batchInputValue = "";

					return;
				}

				const arr = this.batchInputValue.split(/\s+/).filter((i) => i !== "");

				if (this.maxLength && this.batchQueryValue.length + arr.length > this.maxLength) {
					this.$message.error(this.$t("maxInputOrderNumber", { maxLength: this.maxLength }));

					this.batchQueryValue.push(...arr.slice(0, this.maxLength - this.batchQueryValue.length));
					this.batchInputValue = "";
					this.$emit("input", this.batchQueryValue);

					return;
				}

				this.batchQueryValue.push(...arr);
				this.batchInputValue = "";
				this.$emit("input", this.batchQueryValue);
			}
		},
		// 删除指定批量查询值
		handleDeleteBatch(index) {
			this.batchQueryValue.splice(index, 1);
			this.$emit("input", this.batchQueryValue);
		},
		// 清空批量查询
		handleClearBatch() {
			this.batchQueryValue = [];
			this.batchInputValue = "";
			this.visible = false;

			this.$emit("input", this.batchQueryValue);
			this.$emit("search", "");
		},
		// 确认批量查询
		handleConfirm() {
			if (this.batchInputValue) {
				if (this.maxLength && this.batchQueryValue.length >= this.maxLength) {
					this.$message.error(this.$t("maxInputOrderNumber", { maxLength: this.maxLength }));
					this.batchInputValue = "";

					return;
				}

				this.batchQueryValue.push(this.batchInputValue);
				this.$emit("input", this.batchQueryValue);
			}

			this.batchInputValue = "";
			this.visible = false;
			this.$emit("search", this.batchQueryValue);
		}
	},
	watch: {
		value: {
			immediate: true,
			deep: true,
			handler(val) {
				if (val !== undefined) {
					this.inputValue = "";
					this.batchQueryValue = [];
					this.batchInputValue = "";

					if (Array.isArray(val)) {
						this.batchQueryValue = val;
					} else {
						this.inputValue = val;
					}
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.batch-query-input {
	::v-deep .el-input__suffix-inner {
		display: flex;
		flex-direction: row-reverse;
	}

	::v-deep .el-input--suffix .el-input__inner {
		padding-right: 55px;
	}

	&-suffix {
		display: flex;
		align-items: center;

		.el-icon-search {
			cursor: pointer;
		}

		&-line {
			width: 1px;
			height: 14px;
			background-color: #bcc4d0;
		}

		&-title {
			display: flex;
			align-items: center;
			margin-left: 8px;
			margin-right: 4px;

			> span {
				width: 56px;
				text-align: center;
				font-family: PingFang SC;
				font-weight: 400;
				font-size: 14px;
				color: #2c68ff;
				line-height: 22px;
				text-align: center;
				font-style: normal;
				text-transform: none;
				cursor: pointer;
			}

			> i {
				font-size: 16px;
				line-height: 22px;
				color: rgba(0, 0, 0, 0.5);
				cursor: pointer;
			}
		}

		&-content {
			width: 240px;
			padding: 12px;
			border: 1px solid #2c68ff;
			border-radius: 2px;

			&-value {
				max-height: 200px;
				width: 100%;
				overflow: auto;
				display: flex;
				flex-wrap: wrap;

				&-item {
					display: inline-flex;
					align-items: center;
					margin-bottom: 4px;
					background-color: #e7e8eb;
					font-size: 12px;
					line-height: 16px;
					padding: 2px 4px;
					border-radius: 2px;
					max-width: calc(100% - 4px);

					&:not(:last-child) {
						margin-right: 4px;
					}

					> :first-child {
						max-width: calc(100% - 16px);
					}

					> i {
						font-size: 14px;
						line-height: 20px;
						color: rgba(0, 0, 0, 0.5);
						cursor: pointer;
						margin-left: 2px;
					}
				}
			}

			::v-deep .el-textarea__inner {
				border: none;
				padding: 0;
				resize: none;
				font-family: PingFang SC;
			}

			&-footer {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				margin-top: 12px;
			}
		}
	}
}
</style>
